import React, { useState, useCallback } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { TextInput, Select, Checkbox } from '../ui/Input';
import { useProStatus, PRO_FEATURES } from '../../hooks/useProStatus';
import { ProBadge } from '../settings/UpgradeModal';

// Paper size options
const PAPER_SIZES = [
  { value: 'letter', label: 'Letter (8.5" x 11")' },
  { value: 'legal', label: 'Legal (8.5" x 14")' },
  { value: 'tabloid', label: 'Tabloid (11" x 17")' },
  { value: 'a4', label: 'A4 (210 x 297 mm)' },
  { value: 'a3', label: 'A3 (297 x 420 mm)' },
];

// Orientation options
const ORIENTATIONS = [
  { value: 'landscape', label: 'Landscape' },
  { value: 'portrait', label: 'Portrait' },
];

// Scale options
const SCALES = [
  { value: 'fit', label: 'Fit to Page' },
  { value: '1/8', label: '1/8" = 1\'-0"' },
  { value: '1/4', label: '1/4" = 1\'-0"' },
  { value: '3/8', label: '3/8" = 1\'-0"' },
  { value: '1/2', label: '1/2" = 1\'-0"' },
  { value: '3/4', label: '3/4" = 1\'-0"' },
  { value: '1', label: '1" = 1\'-0"' },
];

// Export format options
const FORMATS = [
  { value: 'pdf', label: 'PDF Document' },
  { value: 'png', label: 'PNG Image' },
  { value: 'jpeg', label: 'JPEG Image' },
];

/**
 * ExportModal - Modal for configuring and executing exports
 */
export const ExportModal = ({
  onExport,
  onClose,
  isMobile = false,
  projectName = '',
  companyName = '',
}) => {
  const { isPro, requirePro } = useProStatus();
  const [format, setFormat] = useState(isPro ? 'pdf' : 'png');
  const [exporting, setExporting] = useState(false);

  // PDF options
  const [paperSize, setPaperSize] = useState('letter');
  const [orientation, setOrientation] = useState('landscape');
  const [scale, setScale] = useState('fit');

  // Content options
  const [includeFloorPlan, setIncludeFloorPlan] = useState(true);
  const [includePdfBackground, setIncludePdfBackground] = useState(true);
  const [includeAnnotations, setIncludeAnnotations] = useState(true);
  const [includeDimensions, setIncludeDimensions] = useState(true);
  const [includeRoomLabels, setIncludeRoomLabels] = useState(true);

  // Header/Footer options
  const [showHeader, setShowHeader] = useState(true);
  const [showFooter, setShowFooter] = useState(true);
  const [customProjectName, setCustomProjectName] = useState(projectName || 'Floor Plan');
  const [customCompanyName, setCustomCompanyName] = useState(companyName || '');
  const [showDate, setShowDate] = useState(true);
  const [showPageNumbers, setShowPageNumbers] = useState(true);

  // Image export options
  const [imageScale, setImageScale] = useState('1');
  const [jpegQuality, setJpegQuality] = useState('high');

  const handleExport = useCallback(async () => {
    setExporting(true);

    try {
      const options = {
        format,
        // PDF options
        paperSize,
        orientation,
        scale,
        // Content
        includeFloorPlan,
        includePdfBackground,
        includeAnnotations,
        includeDimensions,
        includeRoomLabels,
        // Header/Footer
        showHeader,
        showFooter,
        projectName: customProjectName,
        companyName: customCompanyName,
        showDate,
        showPageNumbers,
        // Image options
        imageScale: parseFloat(imageScale),
        jpegQuality: jpegQuality === 'high' ? 0.92 : jpegQuality === 'medium' ? 0.8 : 0.6,
      };

      await onExport(options);
      onClose();
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [
    format, paperSize, orientation, scale,
    includeFloorPlan, includePdfBackground, includeAnnotations, includeDimensions, includeRoomLabels,
    showHeader, showFooter, customProjectName, customCompanyName, showDate, showPageNumbers,
    imageScale, jpegQuality, onExport, onClose
  ]);

  // On mobile/native, wrap in a full-screen overlay for proper rendering
  const panelContent = (
    <FloatingPanel
      title="Export Floor Plan"
      onClose={onClose}
      x={window.innerWidth / 2 - 180}
      y={80}
      width={360}
      maxHeight={600}
      isMobile={false}
    >
      <div style={{ padding: '12px', overflowY: 'auto' }}>
        {/* Export Format */}
        <PanelSection title="Export Format">
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {FORMATS.map(f => (
              <Button
                key={f.value}
                variant="default"
                active={format === f.value}
                onClick={() => {
                  if (f.value === 'pdf' && !isPro) {
                    requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export');
                    return;
                  }
                  setFormat(f.value);
                }}
                style={{
                  flex: 1,
                  minWidth: '80px',
                  padding: '10px 8px',
                  fontSize: '11px',
                  position: 'relative',
                }}
              >
                {f.label}
                {f.value === 'pdf' && !isPro && <ProBadge className="absolute -top-1 -right-1" />}
              </Button>
            ))}
          </div>
        </PanelSection>

        {/* PDF-specific options */}
        {format === 'pdf' && (
          <>
            <PanelSection title="Page Setup">
              <Select
                label="Paper Size"
                value={paperSize}
                onChange={setPaperSize}
                options={PAPER_SIZES}
              />
              <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                {ORIENTATIONS.map(o => (
                  <Button
                    key={o.value}
                    variant="default"
                    active={orientation === o.value}
                    onClick={() => setOrientation(o.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '11px',
                    }}
                  >
                    {o.label}
                  </Button>
                ))}
              </div>
              <Select
                label="Scale"
                value={scale}
                onChange={setScale}
                options={SCALES}
                style={{ marginTop: '8px' }}
              />
            </PanelSection>

            <PanelSection title="Header & Footer">
              <TextInput
                label="Project Name"
                value={customProjectName}
                onChange={setCustomProjectName}
                placeholder="Floor Plan"
              />
              <TextInput
                label="Company Name"
                value={customCompanyName}
                onChange={setCustomCompanyName}
                placeholder="Optional"
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                <Checkbox
                  label="Show Header"
                  checked={showHeader}
                  onChange={setShowHeader}
                  style={{ flex: '1 0 45%', marginBottom: '4px' }}
                />
                <Checkbox
                  label="Show Footer"
                  checked={showFooter}
                  onChange={setShowFooter}
                  style={{ flex: '1 0 45%', marginBottom: '4px' }}
                />
                <Checkbox
                  label="Show Date"
                  checked={showDate}
                  onChange={setShowDate}
                  style={{ flex: '1 0 45%', marginBottom: '4px' }}
                />
                <Checkbox
                  label="Page Numbers"
                  checked={showPageNumbers}
                  onChange={setShowPageNumbers}
                  style={{ flex: '1 0 45%', marginBottom: '4px' }}
                />
              </div>
            </PanelSection>
          </>
        )}

        {/* Image-specific options */}
        {(format === 'png' || format === 'jpeg') && (
          <PanelSection title="Image Options">
            <div style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '6px' }}>
                Resolution
              </label>
              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { value: '1', label: '1x (Standard)' },
                  { value: '2', label: '2x (High)' },
                  { value: '3', label: '3x (Print)' },
                ].map(s => (
                  <Button
                    key={s.value}
                    variant="default"
                    active={imageScale === s.value}
                    onClick={() => setImageScale(s.value)}
                    style={{
                      flex: 1,
                      padding: '8px 4px',
                      fontSize: '10px',
                    }}
                  >
                    {s.label}
                  </Button>
                ))}
              </div>
            </div>

            {format === 'jpeg' && (
              <div>
                <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '6px' }}>
                  Quality
                </label>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {[
                    { value: 'high', label: 'High' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'low', label: 'Low' },
                  ].map(q => (
                    <Button
                      key={q.value}
                      variant="default"
                      active={jpegQuality === q.value}
                      onClick={() => setJpegQuality(q.value)}
                      style={{
                        flex: 1,
                        padding: '8px',
                        fontSize: '11px',
                      }}
                    >
                      {q.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <TextInput
              label="File Name"
              value={customProjectName}
              onChange={setCustomProjectName}
              placeholder="Floor Plan"
              style={{ marginTop: '10px' }}
            />
          </PanelSection>
        )}

        {/* What to Include */}
        <PanelSection title="Include in Export">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <Checkbox
              label="Floor plan (walls, doors, windows)"
              checked={includeFloorPlan}
              onChange={setIncludeFloorPlan}
            />
            <Checkbox
              label="PDF background layer"
              checked={includePdfBackground}
              onChange={setIncludePdfBackground}
            />
            <Checkbox
              label="Annotations & markups"
              checked={includeAnnotations}
              onChange={setIncludeAnnotations}
            />
            <Checkbox
              label="Measurements & dimensions"
              checked={includeDimensions}
              onChange={setIncludeDimensions}
            />
            <Checkbox
              label="Room labels"
              checked={includeRoomLabels}
              onChange={setIncludeRoomLabels}
            />
          </div>
        </PanelSection>

        {/* Export Button */}
        <div style={{ marginTop: '16px' }}>
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={exporting}
            style={{ width: '100%', padding: '12px' }}
          >
            {exporting ? 'Exporting...' : `Export as ${format.toUpperCase()}`}
          </Button>
        </div>

        {/* Info text */}
        <p style={{
          color: '#6080a0',
          fontSize: '10px',
          textAlign: 'center',
          marginTop: '12px',
          lineHeight: 1.4,
        }}>
          {format === 'pdf'
            ? 'PDF export includes vector-quality output suitable for printing.'
            : format === 'png'
            ? 'PNG preserves transparency and is ideal for digital use.'
            : 'JPEG is best for sharing via email or web.'}
        </p>
      </div>
    </FloatingPanel>
  );

  // On mobile, wrap in a full-screen overlay for proper Android rendering
  if (isMobile) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        zIndex: 99999,
        paddingTop: '60px',
        overflow: 'auto',
      }}>
        <div style={{
          backgroundColor: '#0f141c',
          borderRadius: '8px',
          width: 'calc(100% - 20px)',
          maxWidth: '360px',
          maxHeight: 'calc(100vh - 100px)',
          overflow: 'hidden',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
          border: '1px solid rgba(0,200,255,0.2)',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
            background: 'rgba(0,200,255,0.05)',
          }}>
            <span style={{ color: '#00c8ff', fontWeight: '600', fontSize: '14px' }}>
              Export Floor Plan
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,100,100,0.15)',
                border: 'none',
                color: '#ff6666',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              ✕
            </button>
          </div>

          {/* Scrollable content */}
          <div style={{
            padding: '12px',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 180px)',
          }}>
            {/* Export Format */}
            <PanelSection title="Export Format">
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {FORMATS.map(f => (
                  <Button
                    key={f.value}
                    variant="default"
                    active={format === f.value}
                    onClick={() => {
                      if (f.value === 'pdf' && !isPro) {
                        requirePro(PRO_FEATURES.PDF_EXPORT, 'PDF Export');
                        return;
                      }
                      setFormat(f.value);
                    }}
                    style={{
                      flex: 1,
                      minWidth: '80px',
                      padding: '10px 8px',
                      fontSize: '11px',
                      position: 'relative',
                    }}
                  >
                    {f.label}
                    {f.value === 'pdf' && !isPro && <ProBadge className="absolute -top-1 -right-1" />}
                  </Button>
                ))}
              </div>
            </PanelSection>

            {/* PDF-specific options */}
            {format === 'pdf' && (
              <>
                <PanelSection title="Page Setup">
                  <Select
                    label="Paper Size"
                    value={paperSize}
                    onChange={setPaperSize}
                    options={PAPER_SIZES}
                  />
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    {ORIENTATIONS.map(o => (
                      <Button
                        key={o.value}
                        variant="default"
                        active={orientation === o.value}
                        onClick={() => setOrientation(o.value)}
                        style={{
                          flex: 1,
                          padding: '8px',
                          fontSize: '11px',
                        }}
                      >
                        {o.label}
                      </Button>
                    ))}
                  </div>
                  <Select
                    label="Scale"
                    value={scale}
                    onChange={setScale}
                    options={SCALES}
                    style={{ marginTop: '8px' }}
                  />
                </PanelSection>

                <PanelSection title="Header & Footer">
                  <TextInput
                    label="Project Name"
                    value={customProjectName}
                    onChange={setCustomProjectName}
                    placeholder="Floor Plan"
                  />
                  <TextInput
                    label="Company Name"
                    value={customCompanyName}
                    onChange={setCustomCompanyName}
                    placeholder="Optional"
                  />
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                    <Checkbox
                      label="Show Header"
                      checked={showHeader}
                      onChange={setShowHeader}
                      style={{ flex: '1 0 45%', marginBottom: '4px' }}
                    />
                    <Checkbox
                      label="Show Footer"
                      checked={showFooter}
                      onChange={setShowFooter}
                      style={{ flex: '1 0 45%', marginBottom: '4px' }}
                    />
                    <Checkbox
                      label="Show Date"
                      checked={showDate}
                      onChange={setShowDate}
                      style={{ flex: '1 0 45%', marginBottom: '4px' }}
                    />
                    <Checkbox
                      label="Page Numbers"
                      checked={showPageNumbers}
                      onChange={setShowPageNumbers}
                      style={{ flex: '1 0 45%', marginBottom: '4px' }}
                    />
                  </div>
                </PanelSection>
              </>
            )}

            {/* Image-specific options */}
            {(format === 'png' || format === 'jpeg') && (
              <PanelSection title="Image Options">
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '6px' }}>
                    Resolution
                  </label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {[
                      { value: '1', label: '1x (Standard)' },
                      { value: '2', label: '2x (High)' },
                      { value: '3', label: '3x (Print)' },
                    ].map(s => (
                      <Button
                        key={s.value}
                        variant="default"
                        active={imageScale === s.value}
                        onClick={() => setImageScale(s.value)}
                        style={{
                          flex: 1,
                          padding: '8px 4px',
                          fontSize: '10px',
                        }}
                      >
                        {s.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {format === 'jpeg' && (
                  <div>
                    <label style={{ display: 'block', color: '#6080a0', fontSize: '10px', marginBottom: '6px' }}>
                      Quality
                    </label>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      {[
                        { value: 'high', label: 'High' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'low', label: 'Low' },
                      ].map(q => (
                        <Button
                          key={q.value}
                          variant="default"
                          active={jpegQuality === q.value}
                          onClick={() => setJpegQuality(q.value)}
                          style={{
                            flex: 1,
                            padding: '8px',
                            fontSize: '11px',
                          }}
                        >
                          {q.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <TextInput
                  label="File Name"
                  value={customProjectName}
                  onChange={setCustomProjectName}
                  placeholder="Floor Plan"
                  style={{ marginTop: '10px' }}
                />
              </PanelSection>
            )}

            {/* What to Include */}
            <PanelSection title="Include in Export">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Checkbox
                  label="Floor plan (walls, doors, windows)"
                  checked={includeFloorPlan}
                  onChange={setIncludeFloorPlan}
                />
                <Checkbox
                  label="PDF background layer"
                  checked={includePdfBackground}
                  onChange={setIncludePdfBackground}
                />
                <Checkbox
                  label="Annotations & markups"
                  checked={includeAnnotations}
                  onChange={setIncludeAnnotations}
                />
                <Checkbox
                  label="Measurements & dimensions"
                  checked={includeDimensions}
                  onChange={setIncludeDimensions}
                />
                <Checkbox
                  label="Room labels"
                  checked={includeRoomLabels}
                  onChange={setIncludeRoomLabels}
                />
              </div>
            </PanelSection>

            {/* Export Button */}
            <div style={{ marginTop: '16px' }}>
              <Button
                variant="primary"
                onClick={handleExport}
                disabled={exporting}
                style={{ width: '100%', padding: '12px' }}
              >
                {exporting ? 'Exporting...' : `Export as ${format.toUpperCase()}`}
              </Button>
            </div>

            {/* Info text */}
            <p style={{
              color: '#6080a0',
              fontSize: '10px',
              textAlign: 'center',
              marginTop: '12px',
              lineHeight: 1.4,
            }}>
              {format === 'pdf'
                ? 'PDF export includes vector-quality output suitable for printing.'
                : format === 'png'
                ? 'PNG preserves transparency and is ideal for digital use.'
                : 'JPEG is best for sharing via email or web.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: use FloatingPanel as before
  return panelContent;
};

export default ExportModal;
